/**
 * Clients — the connected-client list, Meraki/UniFi-grade.
 *
 * Live per-client detail sourced from the AP state via /api/v2/clients: who's on
 * (status + name + vendor), what they're on (SSID / band / channel / AP), how good the
 * link is (experience + signal), and what they're doing (live PHY rates, session usage,
 * uptime). Read-only troubleshooting surface — the thing operators judge a controller by.
 */
import { useMemo } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { useClients } from "@/lib/api/hooks";
import { DataGrid } from "@/components/data/DataGrid";
import { PageHeader, ErrorBanner } from "@/components/ui/Page";
import { fmtBytes, fmtDuration } from "@/lib/format";
import type { Client } from "@/types/domain";

/** Best display name for a client: hostname, else vendor, else a short MAC. */
function clientName(c: Client): string {
  if (c.hostname) return c.hostname;
  if (c.vendor && c.vendor !== "Randomized") return `${c.vendor} device`;
  return c.mac.slice(9); // last 3 octets
}

export default function Clients() {
  const { data, isLoading, error } = useClients();
  const rows = data?.items ?? [];
  const navigate = useNavigate();
  const { orgId, siteId } = useParams();
  // Row activation opens the "where is this MAC?" sheet (nested route) in the current scope.
  const base = siteId ? `/o/${orgId}/s/${siteId}` : `/o/${orgId}`;
  const locationHref = (mac: string) => `${base}/clients/${mac}`;

  const columns = useMemo<ColumnDef<Client, unknown>[]>(
    () => [
      {
        id: "client",
        header: "Client",
        cell: (c) => {
          const cl = c.row.original;
          const online = (cl.status ?? "online") === "online";
          return (
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 shrink-0 rounded-full ${online ? "bg-healthy" : "bg-ink-subtle"}`}
                title={online ? "online" : "offline"} />
              <span className="truncate font-medium text-ink">{clientName(cl)}</span>
            </div>
          );
        },
      },
      { accessorKey: "vendor", header: "Vendor", cell: (c) => c.getValue<string | null>() ?? "—" },
      {
        accessorKey: "mac", header: "MAC",
        cell: (c) => <span className="font-mono text-xs">{c.getValue<string>()}</span>,
      },
      {
        accessorKey: "ip", header: "IP",
        cell: (c) => <span className="font-mono text-xs">{c.getValue<string | null>() ?? "—"}</span>,
      },
      {
        id: "connection", header: "Connection",
        cell: (c) => {
          const cl = c.row.original;
          return (
            <div className="text-xs">
              <div className="text-ink">{cl.ssid ?? "—"}</div>
              <div className="text-ink-subtle">
                {[cl.band, cl.channel != null ? `ch ${cl.channel}` : null,
                  cl.channel_width != null ? `${cl.channel_width}MHz` : null]
                  .filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
          );
        },
      },
      {
        id: "ap", header: "AP",
        cell: (c) => <span className="text-xs">{c.row.original.device_name ?? c.row.original.device_serial}</span>,
      },
      {
        accessorKey: "experience", header: "Experience",
        cell: (c) => <ExperienceChip value={c.getValue<string | null>()} />,
      },
      {
        accessorKey: "rssi", header: "Signal",
        cell: (c) => {
          const r = c.getValue<number | null>();
          return <span className="tabular-nums text-xs">{r != null ? `${r} dBm` : "—"}</span>;
        },
      },
      {
        id: "rate", header: "Rate (↓/↑)",
        cell: (c) => {
          const cl = c.row.original;
          return (
            <span className="tabular-nums text-xs">
              {cl.rx_rate_mbps != null ? `${cl.rx_rate_mbps}` : "—"}
              <span className="text-ink-subtle"> / </span>
              {cl.tx_rate_mbps != null ? `${cl.tx_rate_mbps}` : "—"}
              <span className="text-ink-subtle"> Mbps</span>
            </span>
          );
        },
      },
      {
        accessorKey: "retry_pct", header: "Retries",
        // Airtime retry rate: a link-quality signal RSSI alone hides. Warn/critical tint
        // mirrors the thresholds that cap _experience server-side (>=20% -> "fair").
        cell: (c) => {
          const r = c.getValue<number | null>();
          if (r == null) return <span className="text-ink-subtle text-xs">—</span>;
          const tone = r >= 20 ? "text-critical" : r >= 10 ? "text-warn" : "text-ink";
          return <span className={`tabular-nums text-xs ${tone}`}>{r}%</span>;
        },
      },
      {
        id: "usage", header: "Usage",
        cell: (c) => {
          const cl = c.row.original;
          const total = (cl.rx_bytes ?? 0) + (cl.tx_bytes ?? 0);
          return <span className="tabular-nums text-xs">{cl.rx_bytes == null && cl.tx_bytes == null ? "—" : fmtBytes(total)}</span>;
        },
      },
      {
        accessorKey: "connected_sec", header: "Uptime",
        cell: (c) => <span className="tabular-nums text-xs">{fmtDuration(c.getValue<number | null>())}</span>,
      },
    ],
    [],
  );

  const onlineCount = rows.filter((r) => (r.status ?? "online") === "online").length;

  return (
    <div>
      <PageHeader title="Clients" subtitle={`${onlineCount} connected`} />
      <ExperienceRollup rows={rows} />
      {error ? (
        <ErrorBanner error={error} />
      ) : (
        <DataGrid<Client>
          data={rows}
          columns={columns}
          isLoading={isLoading}
          rowKey={(c) => c.mac}
          onRowActivate={(c) => navigate(locationHref(c.mac))}
          empty="No clients currently associated in this scope."
          renderCard={(c) => {
            const online = (c.status ?? "online") === "online";
            return (
              <div>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${online ? "bg-healthy" : "bg-ink-subtle"}`} />
                    <span className="truncate font-medium text-ink">{clientName(c)}</span>
                  </span>
                  <ExperienceChip value={c.experience} />
                </div>
                <div className="mt-0.5 truncate font-mono text-xs text-ink-subtle">
                  {c.mac} · {c.ip ?? "no IP"}
                </div>
                <div className="mt-0.5 truncate text-xs text-ink-muted">
                  {c.ssid ?? "—"}
                  {c.band ? ` · ${c.band}` : ""}
                  {c.channel != null ? ` · ch ${c.channel}` : ""} · {c.device_name ?? c.device_serial}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-subtle">
                  <span>{c.vendor ?? "—"}</span>
                  <span className="tabular-nums">↓{c.rx_rate_mbps ?? "—"}/↑{c.tx_rate_mbps ?? "—"} Mbps</span>
                  {c.retry_pct != null && (
                    <span className={`tabular-nums ${c.retry_pct >= 20 ? "text-critical" : c.retry_pct >= 10 ? "text-warn" : ""}`}>
                      {c.retry_pct}% retries
                    </span>
                  )}
                  <span className="tabular-nums">{fmtBytes((c.rx_bytes ?? 0) + (c.tx_bytes ?? 0))}</span>
                  <span className="tabular-nums">{fmtDuration(c.connected_sec)}</span>
                </div>
              </div>
            );
          }}
        />
      )}

      {/* Nested client-location sheet renders OVER this list (list stays mounted). */}
      <Outlet />
    </div>
  );
}

/** Fleet client-experience rollup: how many clients fall in each link-quality band, so an
 *  operator sees "N clients have a poor connection" at a glance (not one row at a time). */
function ExperienceRollup({ rows }: { rows: Client[] }) {
  const buckets = { excellent: 0, good: 0, fair: 0, poor: 0 };
  type Exp = keyof typeof buckets;
  const isExp = (v: unknown): v is Exp => typeof v === "string" && v in buckets;
  let scored = 0;
  rows.forEach((r) => {
    if (isExp(r.experience)) { buckets[r.experience] += 1; scored += 1; }
  });
  if (scored === 0) return null;

  const cells: { key: Exp; label: string; tone: string }[] = [
    { key: "excellent", label: "Excellent", tone: "text-healthy" },
    { key: "good", label: "Good", tone: "text-healthy" },
    { key: "fair", label: "Fair", tone: "text-warn" },
    { key: "poor", label: "Poor", tone: "text-critical" },
  ];
  const needAttention = buckets.fair + buckets.poor;

  return (
    <section aria-label="Client experience" className="mb-4">
      <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
        Client experience
        {needAttention > 0 && (
          <span className="text-critical">· {needAttention} need attention</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cells.map((c) => (
          <div key={c.key} className="md-card p-3">
            <div className={`text-2xl font-semibold tabular-nums ${buckets[c.key] ? c.tone : "text-ink-subtle"}`}>
              {buckets[c.key]}
            </div>
            <div className="text-[0.6875rem] uppercase tracking-wide text-ink-subtle">{c.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Link-quality chip. Colour encodes state (semantic), never the brand accent. */
function ExperienceChip({ value }: { value?: string | null }) {
  if (!value || value === "unknown") return <span className="text-ink-subtle">—</span>;
  const tone =
    value === "excellent" || value === "good" ? "text-healthy border-healthy/40"
      : value === "fair" ? "text-warn border-warn/40"
        : "text-critical border-critical/40";
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[0.6875rem] font-medium capitalize ${tone}`}>
      {value}
    </span>
  );
}
